import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CategoryReviews = () => {
  const { slug } = useParams();
  const [reviews, setReviews] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, [slug]);

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reviews?category=${slug}`);
      setReviews(data);
      if (data.length > 0) {
        setCategoryName(data[0].category.name);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div data-testid="loading-state" className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="category-reviews-page" className="min-h-screen bg-background py-16">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="mb-8">
          <Link to="/categories" className="text-sm text-primary hover:underline mb-4 inline-block">
            ← All Categories
          </Link>
          <h1 className="font-serif text-4xl font-bold">{categoryName}</h1>
        </div>

        {reviews.length === 0 ? (
          <p data-testid="no-reviews" className="text-muted-foreground">No reviews in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review) => (
              <Link
                key={review.id}
                to={`/review/${review.slug}`}
                data-testid={`review-card-${review.slug}`}
                className="group block"
              >
                <div className="border border-border overflow-hidden hover-scale smooth-transition">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={review.featured_image}
                      alt={review.title}
                      className="w-full h-full object-cover group-hover:scale-105 smooth-transition"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl font-bold mb-3 group-hover:text-primary smooth-transition">
                      {review.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                      {review.description.substring(0, 120)}...
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {renderStars(review.rating)}
                        <span className="text-sm font-semibold">{review.rating}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{review.author}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryReviews;